/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { element, oneOf } from 'prop-types';
import {
  Button,
  LoadingElement,
  LoadingSpinner,
} from '@checkout/react-components';
import { useStoreActions, useStoreState } from 'easy-peasy';

// Internal libraries
import { StyledLink, StyledText } from '../Common';
import { logInButtonStyles, nonBaslLinkStyles } from './styles';
import {
  getContent,
  getGlobals,
  getLazyLoaderRenderFunction,
  lazyLoader,
  redirectToLogIn,
} from '@/lib';
import {
  BaslButtonContainer,
  BaslHaveAnAccountText,
  BaslLogInContainer,
  OrDivider,
  OrText,
} from './StyledComponents';

const BaslLogInLink = ({ logInElement }) => (
  <BaslLogInContainer>
    <BaslButtonContainer>
      <BaslHaveAnAccountText>
        {getContent('billing.haveAnAccount')}
      </BaslHaveAnAccountText>

      {logInElement}
    </BaslButtonContainer>

    <OrDivider>
      <OrText>{getContent('base.or')}</OrText>
    </OrDivider>
  </BaslLogInContainer>
);

BaslLogInLink.propTypes = {
  logInElement: element,
};

const NonBaslLogInLink = ({ logInElement }) => (
  <StyledText css={nonBaslLinkStyles} data-test-id="have-an-account-text">
    {getContent('billing.haveAnAccount', { logInLink: logInElement })}
  </StyledText>
);

NonBaslLogInLink.propTypes = {
  logInElement: element,
};

const LogInLink = ({ children, linkType = 'link' }) => {
  const isButton = linkType === 'button';
  const Component = isButton ? Button : StyledLink;
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const toggleIsPageLoading = useStoreActions(({ ui }) => {
    return ui.toggleIsPageLoading;
  });
  const handleClick = e => {
    e.preventDefault();
    if (!isFormSubmitting) {
      toggleIsPageLoading(true);
      redirectToLogIn();
    }
  };
  const props = {
    children,
    onClick: handleClick,
    ...(isButton && { styleOverrides: logInButtonStyles }),
    ...(!isButton && { 'data-test-id': 'text-log-in-link', href: '#' }),
  };

  return <Component {...props} />;
};

LogInLink.propTypes = {
  linkType: oneOf(['button', 'link']),
};

const HaveAnAccountLogIn = () => {
  const isBasl = getGlobals().isBasl;
  const Component = isBasl ? BaslLogInLink : NonBaslLogInLink;
  const LogIn = (
    <LogInLink linkType={isBasl ? 'button' : 'link'}>
      {getContent('billing.logIn')}
    </LogInLink>
  );

  return <Component logInElement={LogIn} />;
};

const LazyVenmoMessage = lazyLoader({
  loader: () => import('../VenmoMessage'),
  loading: LoadingElement,
  name: 'VenmoMessage',
  render: getLazyLoaderRenderFunction({ componentName: 'VenmoMessage' }),
});

const LazyConsumerAddBankForm = lazyLoader({
  loader: () => import('../ConsumerAddBankForm'),
  loading: () => <LoadingSpinner />,
  name: 'consumer_add_bank_form',
  render(loaded, props) {
    const Component = loaded.ConsumerAddBankForm;
    return <Component />;
  },
});

export {
  HaveAnAccountLogIn,
  LazyConsumerAddBankForm,
  LazyVenmoMessage,
  LogInLink,
};
